import type { Editor } from '@tiptap/react';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

import type { NoteUpdateSource, NoteUpdateType } from '@/models/analytics/features/notes';

/**
 * The rendered TipTap editor for the clinical note. Published (last mount
 * wins) by whichever single-instance note container is mounted — see
 * `usePublishNoteEditor`. Consumed by note-level actions that must target the
 * visible editor (entity-verification fixes, post-note spotlights, V2 footer
 * actions, the note toolbar via `useLiveNoteEditor`); `null` while no note
 * container is mounted.
 */
export const noteEditorAtom = atom<Editor | null>(null);

/**
 * Document-dictation state for the V2 footer mic, keyed by split-screen window
 * index — the document twin of [[noteEditorAtom]]. Written by `DocumentTabPane`
 * and consumed by the V2 footer so its dictation mic targets the editor that is
 * actually recording and honours the same availability rules as the desktop
 * toolbar. Keyed by window (like the dictation status atoms) so split view
 * doesn't cross the wires.
 *
 * - `editor`: the visible document editor, but only when its surface is actually
 *   mounted (`null` otherwise — e.g. prechart/PDF/overlay, or the
 *   RichText/Textarea fallback where the tiptap editor is detached). Prevents
 *   the mic from capturing dictation into a hidden, unpersisted editor.
 * - `disabled`: mirrors the toolbar's start-eligibility (generation in progress,
 *   no edit permission, review-other-user, start overlay). A *running* dictation
 *   is still stoppable — `DictateControls` ignores `disabled` while dictating.
 * - `hidden`: suppress the mic entirely (GPCCMP documents, as the toolbar does).
 */
export type DocumentDictationState = {
  editor: Editor | null;
  disabled: boolean;
  hidden: boolean;
};

const defaultDocumentDictationState: DocumentDictationState = {
  editor: null,
  disabled: true,
  hidden: false,
};

export const documentDictationAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- key param drives atomFamily identity
  (_windowIndex: number) => atom<DocumentDictationState>(defaultDocumentDictationState)
);

/**
 * Attribution information for tracking the source of note updates.
 * Used to attribute note changes to Evidence chat actions (add/replace).
 */
export interface NoteUpdateAttribution {
  source: NoteUpdateSource;
  updateType: NoteUpdateType;
  timestamp: number;
}

/**
 * Atom family to store pending attribution for note updates, keyed by session ID.
 * When Evidence chat "Add to Note" or "Replace Note" is clicked, this is set.
 * useNoteBlurAnalytics reads and clears this when firing trackNoteUpdated.
 */
export const noteUpdateAttributionAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- key param drives atomFamily identity
  (_sessionId: string) => atom<NoteUpdateAttribution | null>(null)
);

/**
 * Markdown-native editor: the exact note string a chat "Add to note" / "Replace
 * note" just wrote to the session cache, keyed by session ID. The editor's
 * content-sync focus guard suppresses cache updates while the user is focused so
 * a stale autosave echo can't reset the cursor mid-type — but that guard can't
 * tell a genuine external insert from an echo, so the insert is otherwise
 * swallowed until a re-hydration. The container force-applies the change once
 * this value matches the propagated content, then clears it. `null` = nothing
 * pending.
 */
export const pendingMarkdownNoteInsertAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- key param drives atomFamily identity
  (_sessionId: string) => atom<string | null>(null)
);

/**
 * Document-tab twin of {@link pendingMarkdownNoteInsertAtomFamily}, keyed by
 * document ID. A chat "Add to note" / "Replace note" into a markdown-native
 * document writes the new content to the document cache; the document editor's
 * focus guard would otherwise swallow that as an autosave echo, so the container
 * force-applies once this value matches the propagated content, then clears it.
 * `null` = nothing pending.
 */
export const pendingMarkdownDocumentInsertAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- key param drives atomFamily identity
  (_documentId: string) => atom<string | null>(null)
);
